import { Link } from 'react-router-dom';
import { ShieldMark } from '../components/ShieldMark';
import { GrassBlades } from '../components/GrassBlades';

// MowKind home page — clean version. Standard layout, just Mowkin
// for the helper role. No MowKith framing, no etymology, no
// vocabulary section. Same shape the site had before the
// MowKith experiments.

export function HomePage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative z-10">
            <h1
              className="text-forest leading-[0.95] mb-8 font-display"
              style={{
                fontSize: 'clamp(3rem, 7vw, 6.5rem)',
                fontVariationSettings: '"opsz" 144, "SOFT" 30',
                fontWeight: 400,
                letterSpacing: '-0.03em',
              }}
            >
              Yards cared for.<br />
              <em style={{ fontVariationSettings: '"opsz" 144, "SOFT" 100' }}>Dignity restored.</em>
            </h1>
            <p className="text-lg lg:text-xl text-forest/80 leading-relaxed max-w-2xl mb-10">
              Some neighbors can no longer manage their own lawns — elderly
              residents, veterans, recovering patients, single parents stretched
              thin. MowKind connects them, free of charge, with a network of
              local lawn care professionals and vetted volunteer Mowkins who
              donate their work.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/apply"
                className="bg-terracotta text-cream px-8 py-4 text-sm tracking-[0.1em] uppercase hover:bg-forest transition-colors"
              >
                Request a Mow →
              </Link>
              <Link
                to="/hire"
                className="bg-forest text-cream px-8 py-4 text-sm tracking-[0.1em] uppercase hover:bg-terracotta transition-colors"
              >
                Support a Mowkin
              </Link>
              <Link
                to="/provider"
                className="border-2 border-forest text-forest px-8 py-4 text-sm tracking-[0.1em] uppercase hover:bg-forest hover:text-cream transition-colors"
              >
                Become a Mowkin
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] bg-forest relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage:
                    'radial-gradient(circle at 1px 1px, #F4EDDE 1px, transparent 0)',
                  backgroundSize: '20px 20px',
                }}
              />
              <div className="absolute inset-0 flex flex-col justify-between p-10 text-cream">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase opacity-60 mb-2">
                    The Pledge
                  </div>
                  <div
                    className="text-3xl leading-tight font-display"
                    style={{ fontVariationSettings: '"opsz" 144' }}
                  >
                    "We mow the lawn of a stranger because they are not strangers
                    to this neighborhood."
                  </div>
                </div>
                <div className="border-t border-cream/20 pt-6">
                  <ShieldMark size={48} className="mb-3" />
                  <div className="text-xs tracking-widest uppercase opacity-70">
                    Founded in Corpus Christi · 2026
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 text-forest/15">
          <GrassBlades className="w-full h-12" />
        </div>
      </section>

      {/* Quick "what we do" strip */}
      <section className="bg-forest text-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <div className="text-xs tracking-[0.3em] uppercase text-sand mb-8 text-center">
            What We Do · In 30 Seconds
          </div>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              [
                '01',
                'Neighbors apply',
                'Elderly, disabled, veteran, and recovering residents tell us they need help with their yard. Free of charge, dignity intact.',
              ],
              [
                '02',
                'Mowkins volunteer',
                'Local Coastal Bend lawn care pros and vetted volunteers donate a share of their monthly capacity to mow the yards on our priority queue.',
              ],
              [
                '03',
                'You can hire them',
                'When you need lawn or hardscape work, hire a Mowkin. Every paid job funds free mows for your neighbors.',
              ],
            ].map(([num, title, body]) => (
              <div key={num}>
                <div className="text-sand text-2xl font-display mb-2">{num}</div>
                <div
                  className="text-2xl text-cream mb-3 font-display leading-tight"
                  style={{ fontVariationSettings: '"opsz" 144' }}
                >
                  {title}
                </div>
                <div className="text-cream/75 text-sm leading-relaxed">{body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-cream-alt py-24 border-y border-forest/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-4">
              <div className="text-xs tracking-[0.25em] uppercase text-terracotta mb-4">How It Works</div>
              <h2 className="text-5xl text-forest leading-tight font-display" style={{ fontVariationSettings: '"opsz" 144' }}>
                Two doors. <em>One mission.</em>
              </h2>
            </div>
            <div className="lg:col-span-8 text-forest/80 text-lg leading-relaxed">
              Residents apply through a brief, dignified intake. Each
              application is reviewed personally by our small team and queued
              in priority order based on need. Mowkins join the network,
              commit to a small number of donated mows per month, and receive
              qualified leads for paid work in return.
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-cream p-10 border border-forest/10">
              <div className="text-terracotta text-xs tracking-[0.25em] uppercase mb-6">For Residents</div>
              <h3 className="text-3xl text-forest mb-8 font-display">If you need a hand</h3>
              <ol className="space-y-6">
                {[
                  ['01', 'Apply', 'Fill out a brief application. Tell us your situation honestly. Anything shared stays private.'],
                  ['02', 'We review', 'A person on our small review team reads every application. We confirm by phone before any Mowkin is dispatched.'],
                  ['03', 'Get mowed', 'A Mowkin is assigned. They show up, mow, edge, and blow. No charge, no catch.'],
                ].map(([num, title, body]) => (
                  <li key={num} className="flex gap-5">
                    <div className="text-terracotta text-2xl shrink-0 w-10 font-display">{num}</div>
                    <div>
                      <div className="text-forest text-lg mb-1 font-display">{title}</div>
                      <div className="text-forest/70 text-sm leading-relaxed">{body}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="bg-forest text-cream p-10">
              <div className="text-sand text-xs tracking-[0.25em] uppercase mb-6">For Lawn Care Pros</div>
              <h3 className="text-3xl mb-8 font-display">If you want to give back</h3>
              <ol className="space-y-6">
                {[
                  ['01', 'Join the network', 'Apply to become a Mowkin. Tell us your service area, capacity, and equipment.'],
                  ['02', 'Commit your share', 'Choose how many donated mows per month you can offer. Two? Four? Eight? You set the pace.'],
                  ['03', 'Earn alongside it', 'When community members call us looking for paid quotes, those leads route to Mowkins first — no fee, no kickback.'],
                ].map(([num, title, body]) => (
                  <li key={num} className="flex gap-5">
                    <div className="text-sand text-2xl shrink-0 w-10 font-display">{num}</div>
                    <div>
                      <div className="text-cream text-lg mb-1 font-display">{title}</div>
                      <div className="text-cream/75 text-sm leading-relaxed">{body}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* A Fair Process */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-xs tracking-[0.25em] uppercase text-terracotta mb-4">A Fair Process</div>
              <h2 className="text-5xl text-forest mb-6 leading-tight font-display">
                How we decide <em>who comes first.</em>
              </h2>
              <p className="text-forest/75 text-lg leading-relaxed mb-4">
                Every application is read by the same small review team, using
                the same standards, applied consistently.
              </p>
              <p className="text-forest/75 text-base leading-relaxed">
                We don't publish the specifics of how we prioritize —
                disclosing the weights would let applicants tune their answers
                and push neighbors with genuine need further down the queue.
              </p>
            </div>
            <div className="lg:col-span-7 space-y-6">
              {[
                ['Personal review', 'Every application is read by a person on our small team — not an algorithm, not a form letter.'],
                ['Confidential', 'Your situation, story, and contact information stay with the review team. We never publish names, addresses, or stories.'],
                ['Consistent', 'We apply the same standards to everyone. No favoritism, no shortcuts for knowing the right people.'],
                ['Phone confirmation', 'Before any Mowkin is dispatched, we call to confirm with you. You stay in control of who comes to your home.'],
              ].map(([title, body]) => (
                <div key={title} className="border-l-2 border-forest/20 pl-6">
                  <div className="text-forest font-display text-xl mb-1">{title}</div>
                  <div className="text-forest/70 text-sm leading-relaxed">{body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
